import type { Cents } from "../domain/money";
import type { CostType } from "../domain/proposedCostLine";
import type { ProposedCostLine } from "../domain/proposedCostLine";
import type { ActualCostEvent } from "./actualEvents";
import { expenseTotalCents } from "./financials";

/**
 * Actual reconciliation & variance (Phase 9C, spec §29A) — PURE.
 *
 * The one rule everything else serves: **never present a variance between
 * cost bases that are not comparable.** A material receipt does not tell
 * you whether the job came in under budget while the crew has not invoiced
 * its labor, and saying "-$100" there is a lie the software must refuse to
 * tell.
 *
 * Therefore reconciliation happens per COST BASIS (material / labor /
 * other) inside each feature, each basis carries an explicit completeness
 * state, and a total variance exists only when every basis that has a
 * proposed amount is complete. Recorded actuals are always shown — they
 * are simply not called "variance" until they are comparable.
 *
 * Nothing here stores anything: every number is derived from the
 * authoritative proposed lines and ActualCostEvents, so there is no second
 * accounting truth to drift.
 */

/** Cost basis used for comparison. Mileage and lump sums are never labor. */
export type CostBasis = CostType; // "material" | "labor" | "other"

export const COST_BASES: readonly CostBasis[] = ["material", "labor", "other"] as const;

export type CompletenessState =
  /** No actual money recorded against this basis yet. */
  | "not-started"
  /** Some money recorded, but the basis is not finished (or is unknown). */
  | "partial"
  /** Everything expected has been recorded; variance is meaningful. */
  | "complete"
  /** Recorded through a grouped labor settlement — compared at the group. */
  | "settled-in-group"
  /** Money recorded that cannot be attributed to a comparable basis. */
  | "not-comparable";

export const COMPLETENESS_LABELS: Record<CompletenessState, string> = {
  "not-started": "Not recorded",
  partial: "Partly recorded",
  complete: "Complete",
  "settled-in-group": "Settled in labor group",
  "not-comparable": "Not comparable",
};

/** Evidence that a basis is finished, supplied by procurement/user state. */
export interface BasisCompletenessInput {
  /** Procurement says every required unit has been purchased. */
  procurementComplete?: boolean;
  /** Procurement knows the requirement but it is not yet fulfilled. */
  procurementPartial?: boolean;
  /** The user marked this basis finished (e.g. "crew has been paid"). */
  userConfirmedComplete?: boolean;
  /** Requirement is not this project's to buy (crew supplied / not required). */
  excluded?: boolean;
}

export interface QuantityComparison {
  unit: string;
  /** Construction requirement incl. waste (×100). */
  requiredX100: number;
  /** Actually purchased, in the same unit (×100). */
  purchasedX100: number;
  /** purchased − required (may be negative). */
  differenceX100: number;
  /** Hundredths of a percent of required; null when required is 0. */
  differencePctX100: number | null;
}

export interface BasisReconciliation {
  basis: CostBasis;
  proposedCents: Cents;
  /** Money actually recorded against this basis (tax-inclusive). */
  recordedActualCents: Cents;
  completeness: CompletenessState;
  /** recorded − proposed, ONLY when comparable; null otherwise. */
  varianceCents: Cents | null;
  /** Hundredths of a percent; null when not comparable or proposed is 0. */
  variancePctX100: number | null;
  /** Why a variance is unavailable, in the user's words. */
  varianceUnavailableReason: string | null;
  /** Contributing actual events (provenance drill-down). */
  eventIds: string[];
  /** Set when a labor settlement is the comparison unit for this basis. */
  settlementId: string | null;
  quantity: QuantityComparison | null;
}

export interface FeatureReconciliation {
  featureId: string;
  featureName: string;
  categoryId: string | null;
  scopeLabel: string;
  proposedCents: Cents;
  recordedActualCents: Cents;
  bases: BasisReconciliation[];
  /** Comparable only when every proposed basis is complete. */
  totalVarianceCents: Cents | null;
  totalVariancePctX100: number | null;
  totalStatus: CompletenessState;
  totalUnavailableReason: string | null;
}

export interface LaborSettlementReconciliation {
  coverageKind?: "labor" | "mixed-work";
  settlementId: string;
  description: string;
  /** Σ proposed labor of the grouped lines. */
  proposedCents: Cents;
  /** The ONE actual record's amount — never split across items. */
  actualCents: Cents;
  varianceCents: Cents;
  variancePctX100: number | null;
  /** Features whose labor this settlement covers. */
  featureIds: string[];
  proposedLineIds: string[];
  /** Provenance of the actual side. */
  eventId: string | null;
}

export interface ReconciliationInput {
  /** Proposed lines of the ACTIVE design + common scope, already filtered. */
  proposedLines: ProposedCostLine[];
  /** All non-void actual events for the project. */
  events: ActualCostEvent[];
  features: Array<{ id: string; name: string; categoryId: string | null; scopeLabel: string }>;
  /** proposedLineId → settlementId (grouped labor). */
  settlementByLineId: Map<string, string>;
  settlements: Array<{ id: string; description: string; eventId: string | null; coverageKind?: "labor"|"mixed-work" }>;
  /** featureId → basis → completeness evidence. */
  completeness: Map<string, Partial<Record<CostBasis, BasisCompletenessInput>>>;
  /** featureId → material quantity comparison, when known. */
  quantities: Map<string, QuantityComparison>;
  /** Cost basis of an event; null when the source did not state one. */
  eventBasis: (event: ActualCostEvent) => CostBasis | null;
}

export interface ReconciliationResult {
  features: FeatureReconciliation[];
  settlements: LaborSettlementReconciliation[];
  /** Actual money that reached the project but no feature (overheads etc.). */
  unassignedActualCents: Cents;
  unassignedEventIds: string[];
  totals: {
    proposedCents: Cents;
    recordedActualCents: Cents;
    /** Only the portion of proposed whose basis is complete. */
    comparableProposedCents: Cents;
    comparableActualCents: Cents;
    comparableVarianceCents: Cents;
    /** Features still missing a comparable actual. */
    incompleteFeatureCount: number;
  };
}

function pctX100(numerator: Cents, denominator: Cents): number | null {
  if (denominator === 0) return null;
  // Half-up on the magnitude keeps +/- symmetric.
  const scaled = (numerator * 10000) / denominator;
  return scaled >= 0 ? Math.floor(scaled + 0.5) : -Math.floor(-scaled + 0.5);
}

const isActive = (e: ActualCostEvent): boolean => e.voidedAt === null;

/**
 * Reconcile one project: proposed (active design) vs recorded actuals,
 * per feature and per cost basis.
 */
export function reconcile(input: ReconciliationInput): ReconciliationResult {
  const events = input.events.filter(isActive);
  const lines = input.proposedLines.filter((l) => l.archivedAt === null);

  // ---- proposed side: feature → basis → cents -----------------------
  const proposedByFeature = new Map<string, Map<CostBasis, Cents>>();
  const settlementProposed = new Map<string, Cents>();
  const settlementFeatures = new Map<string, Set<string>>();
  const settlementLines = new Map<string, string[]>();
  const validMixedIds=new Set(input.settlements.filter(s=>s.coverageKind==="mixed-work"&&events.some(e=>e.id===s.eventId&&e.voidedAt===null&&expenseTotalCents(e)>0)).filter(s=>{
    const linked=lines.filter(l=>input.settlementByLineId.get(l.id)===s.id);
    const features=new Set(linked.map(l=>l.featureId));
    return !lines.some(l=>features.has(l.featureId)&&input.settlementByLineId.get(l.id)!==s.id);
  }).map(s=>s.id));
  for (const line of lines) {
    if (!line.featureId) continue;
    const basis: CostBasis = line.costType ?? "other";
    const perBasis = proposedByFeature.get(line.featureId) ?? new Map<CostBasis, Cents>();
    perBasis.set(basis, (perBasis.get(basis) ?? 0) + line.amountCents);
    proposedByFeature.set(line.featureId, perBasis);

    const candidate = input.settlementByLineId.get(line.id);
    const settlementId = candidate && (input.settlements.find(s=>s.id===candidate)?.coverageKind!=="mixed-work"||validMixedIds.has(candidate)) ? candidate : undefined;
    if (settlementId) {
      settlementProposed.set(settlementId, (settlementProposed.get(settlementId) ?? 0) + line.amountCents);
      const feats = settlementFeatures.get(settlementId) ?? new Set<string>();
      feats.add(line.featureId);
      settlementFeatures.set(settlementId, feats);
      settlementLines.set(settlementId, [...(settlementLines.get(settlementId) ?? []), line.id]);
    }
  }

  // ---- actual side: attribute each event to a feature + basis --------
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const actualByFeature = new Map<string, Map<CostBasis | "unknown", { cents: Cents; ids: string[] }>>();
  const settlementActual = new Map<string, { cents: Cents; eventId: string | null }>();
  const settledEventIds = new Set<string>();
  let unassignedCents = 0;
  const unassignedIds: string[] = [];

  // Events consumed by a labor settlement are compared at the group, so
  // they must not also land on an individual feature's labor basis.
  const settlementEventIds = new Set(
    input.settlements.filter(s=>s.coverageKind!=="mixed-work"||validMixedIds.has(s.id)).map((s) => s.eventId).filter((x): x is string => x !== null),
  );

  for (const event of events) {
    const total = expenseTotalCents(event);
    if (settlementEventIds.has(event.id)) {
      const settlement = input.settlements.find((s) => s.eventId === event.id)!;
      const prev = settlementActual.get(settlement.id) ?? { cents: 0, eventId: event.id };
      settlementActual.set(settlement.id, { cents: prev.cents + total, eventId: event.id });
      settledEventIds.add(event.id);
      continue;
    }
    // A line link is the most specific: it names the basis exactly.
    const linkedLine = event.linkedProposedLineId ? lineById.get(event.linkedProposedLineId) : undefined;
    const featureId = linkedLine?.featureId ?? event.linkedFeatureId ?? null;
    if (!featureId) {
      unassignedCents += total;
      unassignedIds.push(event.id);
      continue;
    }
    const basis: CostBasis | "unknown" = linkedLine?.costType ?? input.eventBasis(event) ?? "unknown";
    const perBasis = actualByFeature.get(featureId) ?? new Map<CostBasis | "unknown", { cents: Cents; ids: string[] }>();
    const cell = perBasis.get(basis) ?? { cents: 0, ids: [] };
    cell.cents += total;
    cell.ids.push(event.id);
    perBasis.set(basis, cell);
    actualByFeature.set(featureId, perBasis);
  }

  // ---- per-feature reconciliation ------------------------------------
  const featureIds = new Set<string>([...proposedByFeature.keys(), ...actualByFeature.keys()]);
  const featureRows: FeatureReconciliation[] = [];
  for (const featureId of featureIds) {
    const meta = input.features.find((f) => f.id === featureId);
    const proposedBases = proposedByFeature.get(featureId) ?? new Map<CostBasis, Cents>();
    const actualBases = actualByFeature.get(featureId) ?? new Map<CostBasis | "unknown", { cents: Cents; ids: string[] }>();
    const evidence = input.completeness.get(featureId) ?? {};
    const unknown = actualBases.get("unknown");
    const hasUnclassifiedActual = unknown !== undefined && unknown.cents !== 0;

    const bases: BasisReconciliation[] = [];
    for (const basis of COST_BASES) {
      const proposed = proposedBases.get(basis) ?? 0;
      const actual = actualBases.get(basis) ?? { cents: 0, ids: [] };
      if (proposed === 0 && actual.cents === 0) continue;

      // Is this feature's labor settled through a group?
      const settlementId = [...settlementFeatures.entries()].find(([id,feats]) => feats.has(featureId) && (basis === "labor" || input.settlements.find(s=>s.id===id)?.coverageKind === "mixed-work"))?.[0] ?? null;

      const ev = evidence[basis] ?? {};
      let completeness: CompletenessState;
      let reason: string | null = null;
      if (settlementId) {
        completeness = "settled-in-group";
        reason = "This cost is compared only at its explicitly settled work group; no item actual is inferred.";
      } else if (hasUnclassifiedActual) {
        completeness = "partial";
        reason = "Some recorded cost has no material/labor classification. Classify it before comparing cost bases.";
      } else if (ev.excluded) {
        completeness = "complete";
      } else if (ev.userConfirmedComplete) {
        completeness = "complete";
      } else if (ev.procurementComplete && actual.cents !== 0) {
        completeness = "complete";
      } else if (actual.cents === 0) {
        completeness = "not-started";
        reason = proposed > 0 ? `No ${basis} cost recorded yet.` : null;
      } else if (ev.procurementPartial) {
        completeness = "partial";
        reason = "Not everything required has been purchased yet.";
      } else {
        completeness = "partial";
        reason = `Some ${basis} cost is recorded, but it is not confirmed complete.`;
      }

      const comparable = completeness === "complete" && proposed > 0;
      const quantity = basis === "material" ? (input.quantities.get(featureId) ?? null) : null;
      bases.push({
        basis,
        proposedCents: proposed,
        recordedActualCents: actual.cents,
        completeness,
        varianceCents: comparable ? actual.cents - proposed : null,
        variancePctX100: comparable ? pctX100(actual.cents - proposed, proposed) : null,
        varianceUnavailableReason: comparable ? null : reason,
        eventIds: actual.ids,
        settlementId,
        quantity,
      });
    }

    // Money recorded on the feature without a comparable basis.
    if (unknown && unknown.cents !== 0) {
      bases.push({
        basis: "other",
        proposedCents: 0,
        recordedActualCents: unknown.cents,
        completeness: "not-comparable",
        varianceCents: null,
        variancePctX100: null,
        varianceUnavailableReason:
          "Recorded without a material/labor classification, so it cannot be compared to a proposed basis.",
        eventIds: unknown.ids,
        settlementId: null,
        quantity: null,
      });
    }

    const proposedTotal = [...proposedBases.values()].reduce((s, v) => s + v, 0);
    const recordedTotal =
      [...actualBases.values()].reduce((s, v) => s + v.cents, 0) +
      // A grouped settlement's money belongs to the group, not the feature.
      0;

    const proposedBasesNeedingActual = bases.filter((b) => b.proposedCents > 0);
    const allComplete =
      proposedBasesNeedingActual.length > 0 &&
      proposedBasesNeedingActual.every((b) => b.completeness === "complete");
    const anySettled = bases.some((b) => b.completeness === "settled-in-group");
    const outstanding = proposedBasesNeedingActual.filter((b) => b.completeness !== "complete");

    let totalStatus: CompletenessState;
    let totalReason: string | null = null;
    if (hasUnclassifiedActual) {
      totalStatus = "partial";
      totalReason = "Some recorded cost has no material/labor classification. All recorded actuals are shown; variance is unavailable until classification is complete.";
    } else if (proposedBasesNeedingActual.length === 0) {
      totalStatus = recordedTotal === 0 ? "not-started" : "not-comparable";
    } else if (allComplete) {
      totalStatus = "complete";
    } else if (anySettled) {
      totalStatus = "settled-in-group";
      totalReason = "Part of this item's cost is settled in a labor group; see the group for its comparison.";
    } else if (recordedTotal === 0) {
      totalStatus = "not-started";
      totalReason = "Nothing recorded against this item yet.";
    } else {
      totalStatus = "partial";
      totalReason = `Actual cost is incomplete — ${outstanding
        .map((b) => (b.completeness === "not-started" ? `${b.basis} not recorded` : `${b.basis} partly recorded`))
        .join(", ")}.`;
    }

    const totalComparable = totalStatus === "complete";
    featureRows.push({
      featureId,
      featureName: meta?.name ?? "(unknown item)",
      categoryId: meta?.categoryId ?? null,
      scopeLabel: meta?.scopeLabel ?? "",
      proposedCents: proposedTotal,
      recordedActualCents: recordedTotal,
      bases,
      totalVarianceCents: totalComparable ? recordedTotal - proposedTotal : null,
      totalVariancePctX100: totalComparable ? pctX100(recordedTotal - proposedTotal, proposedTotal) : null,
      totalStatus,
      totalUnavailableReason: totalReason,
    });
  }

  featureRows.sort((a, b) => a.scopeLabel.localeCompare(b.scopeLabel) || a.featureName.localeCompare(b.featureName));

  // ---- grouped labor settlements -------------------------------------
  const settlementRows: LaborSettlementReconciliation[] = input.settlements.filter(s=>s.coverageKind!=="mixed-work"||validMixedIds.has(s.id)).map((s) => {
    const proposed = settlementProposed.get(s.id) ?? 0;
    const actual = settlementActual.get(s.id)?.cents ?? 0;
    return {
      settlementId: s.id,
      coverageKind:s.coverageKind ?? "labor",
      description: s.description,
      proposedCents: proposed,
      actualCents: actual,
      varianceCents: actual - proposed,
      variancePctX100: pctX100(actual - proposed, proposed),
      featureIds: [...(settlementFeatures.get(s.id) ?? [])],
      proposedLineIds: settlementLines.get(s.id) ?? [],
      eventId: s.eventId,
    };
  });

  // ---- project totals -------------------------------------------------
  const proposedTotal = lines.reduce((s, l) => s + l.amountCents, 0);
  const recordedTotal = events.reduce((s, e) => s + expenseTotalCents(e), 0);
  let comparableProposed = 0;
  let comparableActual = 0;
  for (const f of featureRows) {
    for (const b of f.bases) {
      if (b.varianceCents === null) continue;
      comparableProposed += b.proposedCents;
      comparableActual += b.recordedActualCents;
    }
  }
  for (const s of settlementRows) {
    comparableProposed += s.proposedCents;
    comparableActual += s.actualCents;
  }

  return {
    features: featureRows,
    settlements: settlementRows,
    unassignedActualCents: unassignedCents,
    unassignedEventIds: unassignedIds,
    totals: {
      proposedCents: proposedTotal,
      recordedActualCents: recordedTotal,
      comparableProposedCents: comparableProposed,
      comparableActualCents: comparableActual,
      comparableVarianceCents: comparableActual - comparableProposed,
      incompleteFeatureCount: featureRows.filter(
        (f) => f.totalStatus === "partial" || f.totalStatus === "not-started",
      ).length,
    },
  };
}

/** Quantity comparison from procurement figures (kept separate from cost). */
export function quantityComparison(input: {
  unit: string;
  requiredX100: number;
  purchasedX100: number;
}): QuantityComparison {
  const difference = input.purchasedX100 - input.requiredX100;
  return {
    unit: input.unit,
    requiredX100: input.requiredX100,
    purchasedX100: input.purchasedX100,
    differenceX100: difference,
    differencePctX100: input.requiredX100 === 0 ? null : pctX100(difference, input.requiredX100),
  };
}

/** Format helper for percent values stored as hundredths of a percent. */
export function formatPctX100(value: number | null): string {
  if (value === null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${(value / 100).toFixed(1)}%`;
}
